import React from 'react';
import PropTypes from 'prop-types';
import TimerLabelNode from './timer-label-node';
import {handleEvent} from '../../../../events/event-handler';
import {isEmpty} from '../../../../utilities/validation';
import IntervalNode from './interval-node';
import {filterNodeProperties} from '../../../../utilities/filter-node-properties';

const getDataFromNode = function(node) {
    const keys = ['id', 'title', 'behavior', 'count_direction', 'time', 'start', 'loop', 'enabled', 'required'];
    const props = filterNodeProperties(node, keys, { val: node.val });
    props.autoStart = (props.start === '1' && props.enabled === '1');

    return props;
};

const getStateFromProps = function(props = {}) {
    const intervalNodes = props.node.childrenNamed('interval_node') || null;

    return {
        ...getDataFromNode(props.node),
        intervalNodes,
    };
};

class TimerNode extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            ...getStateFromProps(props),
            resetting: false,
            activeIntervalNodes: [],
            intervalNodesPlayed: {}, // we can also do this with an array and compare length of intervalNodes to
            // that of intervalNodesPlayed, but storing key(intervalNodeId) : value(true if rendered, else false)
            // can never yield a false positive
        };

        // if we are counting down, start from the "time" attribute.
        // otherwise, if counting up, start at 0
        this.state.currentSeconds = (this.state.count_direction === 'down' ? parseInt(this.state.time) : 0);

        if (this.state.autoStart){
            this.startTimer();
        }

        this.startTimerEvent = this.startTimer.bind(this);
        this.stopTimerEvent = this.stopTimer.bind(this);
        this.resetTimerEvent = this.resetTimer.bind(this);
        this.fetchCurrentIntervalNodes = this.fetchCurrentIntervalNodes.bind(this);
    }

    componentDidMount() {
        const {disableJREAppPlayerContainerButtons} = this.props;
        const {intervalNodes} = this.state;
        if(!isEmpty(intervalNodes)){
            disableJREAppPlayerContainerButtons('requiredNodeNotCompleted', 'TimerNode componentDidMount');
        }
        // When the component is mounted, add your DOM listener to the "nv" elem.
        // (The "nv" elem is assigned in the render function.)
        document.addEventListener('jre-timer-start', this.startTimerEvent);
        document.addEventListener('jre-timer-stop', this.stopTimerEvent);
        document.addEventListener('jre-timer-reset', this.resetTimerEvent); //(Investigating Careers- 324)
    }

    // eslint-disable-next-line no-unused-vars
    componentDidUpdate(prevProps, prevState, snapshot) {
        const {enableJREAppPlayerContainerButtons}= this.props;
        const {intervalNodes, activeIntervalNodes, intervalNodesPlayed} = this.state;

        if(!isEmpty(activeIntervalNodes)){
            const {key} = activeIntervalNodes[0]; // always has only one element...
            if(!intervalNodesPlayed.hasOwnProperty(key)){
                this.setState({intervalNodesPlayed: {
                        ...prevState.intervalNodesPlayed,
                        [key]: true,
                    }}, () => {
                    const {intervalNodesPlayed} = this.state; // fetch updated state, otherwise original
                    // intervalNodesPlayed which is missing the last element
                    const keyIntervalNodesPlayedArray = Object.keys(intervalNodesPlayed);

                    const intervalNodesKeysArray = [];
                    for (let i=0; i<intervalNodes.length; i++) {
                        const intervalNode = intervalNodes[i];
                        const {attr: {id}} = intervalNode;

                        intervalNodesKeysArray.push(id);
                    }
                    const allIntervalNodesPlayed = intervalNodesKeysArray.every(intervalNodesKey => {
                        return keyIntervalNodesPlayedArray.includes(intervalNodesKey);
                    });

                    if (allIntervalNodesPlayed){
                        enableJREAppPlayerContainerButtons('requiredNodeNotCompleted', 'TimerNode componentDidUpdate');
                    }
                });
            }
        }
    }


    componentWillUnmount() {
        const {enableJREAppPlayerContainerButtons} = this.props;
        enableJREAppPlayerContainerButtons('requiredNodeNotCompleted', 'TimerNode componentDidMount');

        // Make sure to remove the DOM listener when the component is unmounted.
        document.removeEventListener('jre-timer-start', this.startTimerEvent);
        document.removeEventListener('jre-timer-stop', this.stopTimerEvent);
        document.removeEventListener('jre-timer-reset', this.resetTimerEvent);
        clearInterval(this.timer);
    }

    tick() {
        if (this.state.count_direction === 'down') {
            const nextSeconds = this.state.currentSeconds - 1;
            this.setState({currentSeconds: nextSeconds, activeIntervalNodes: this.fetchCurrentIntervalNodes(nextSeconds)});
            if (nextSeconds === 0) {
                clearInterval(this.timer);
                this.timerCompleted();
            }
        } else {
            const nextSeconds = this.state.currentSeconds + 1;
            this.setState({currentSeconds: nextSeconds, activeIntervalNodes: this.fetchCurrentIntervalNodes(nextSeconds)});
            if (nextSeconds === parseInt(this.state.time)) {
                clearInterval(this.timer);
                this.timerCompleted();
            }
        }
    }

    startTimer(e) {
        const {
            autoStart, // autoStart triggers startTimer in constructor
            resetting, // true when timer has been reset, but not restarted
        } = this.state;
        const {node} = this.props;


        if (autoStart || node.attr.id === e.detail.target) {
            if (resetting === true) {
                //reset the timer to the initial value, and return "resetting" back to false
                this.setState({
                    currentSeconds: (this.state.count_direction === 'down' ? parseInt(this.state.time) : 0),
                    resetting: false,
                });
            }
            clearInterval(this.timer);
            this.timer = setInterval(this.tick.bind(this), 1000);
        }
    }

    stopTimer(e) {
        if ((typeof this.props.node.attr.id !== undefined) && (typeof e.detail.target !== undefined)
            && this.props.node.attr.id.toLowerCase() === e.detail.target.toLowerCase()) {
            clearInterval(this.timer);
        }
    }

    resetTimer(e) {
        if ((typeof this.props.node.attr.id !== undefined) && (typeof e.detail.target !== undefined)
            && this.props.node.attr.id.toLowerCase() === e.detail.target.toLowerCase()) {
            this.setState({resetting: true});
            clearInterval(this.timer);
        }
    }

    timerCompleted() {
        let event_nodes = this.props.node.childrenNamed('event_node');
        event_nodes.forEach(function(event) {
            if (event.attr.name === 'ontimercomplete') {
                handleEvent(event);
            }
        });
    }

    onFinalInterval() {
        const event_nodes = this.props.node.childrenNamed('event_node');
        event_nodes.forEach(function (event) {
            if (event.attr.name === 'onexplorecomplete') {
                handleEvent(event);
            }
        });
    }

    fetchCurrentIntervalNodes(currentSeconds) {
        const {moduleURL = '', enableJREAppPlayerContainerButtons, disableJREAppPlayerContainerButtons} = this.props;
        const {activeIntervalNodes, intervalNodes, count_direction, time} = this.state;

        const currentIntervalNodes = intervalNodes.map((n, idx) => {
            // only show the interval node if its time has come!
            if (count_direction === 'up' && currentSeconds === parseInt(n.attr.time) ||
                count_direction === 'down' && (parseInt(time) - parseInt(n.attr.time)) === currentSeconds
            ) {
                // interval nodes seem to be ordered by their activation time
                // when the last intervalNode is reached, we handle the onexplorecomplete event
                if (idx === intervalNodes.length - 1) {
                    this.onFinalInterval();
                }
                return <IntervalNode key={n.attr.id} node={n} moduleURL={moduleURL}
                                     enableJREAppPlayerContainerButtons={enableJREAppPlayerContainerButtons}
                                     disableJREAppPlayerContainerButtons={disableJREAppPlayerContainerButtons}
                />;
            }
        }).filter(function(interval){
            return interval !== undefined;
        });

        // We must show interval nodes until they are replaced.
        // If there is no currentIntervalNode for a particular second, use the activeIntervalNode again
        return isEmpty(currentIntervalNodes) ? activeIntervalNodes :  currentIntervalNodes;
    }

    render() {
        const {node} = this.props;
        const {activeIntervalNodes, currentSeconds} = this.state;
        const timerLabelNode = node.children.find(n => n.name === 'timer_label_node');

        return (
            <div>
                {timerLabelNode &&
                <TimerLabelNode node={timerLabelNode} currentSeconds={currentSeconds} />}
                {activeIntervalNodes}
            </div>
        );
    }
}


TimerNode.propTypes = {
    node: PropTypes.object.isRequired
};


export default TimerNode;



// WEBPACK FOOTER //
// ./src/js/components/app/jre/elements/timer-node.js